import React, { Component } from 'react';
import AlertMessage from './AlertMessage';
import withWorkflow from './components/withWorkflow';
import { scrollToMessage } from './util/scrollToMessage';
import RequiredFieldDisclaimer from './components/RequiredFieldDisclaimer';
import { fetchPractitionerOptions } from './util/apiUtils';
import AccessionedOrderAlert from './components/AccessionedOrderAlert';
import AdminPractitionerSearch from './components/AdminPractitionerSearch';
import {getCustomerCareNumber, isUKOrder} from './util/gearOrderUtil';

/**
 * Component for the 'practitioner' step of the GEAR order workflow.
 */
class OfficeInfo extends Component {

	/**
	 * Constructor initializes state and binds functions.
	 */
	constructor(props) {
		super(props);
		this.state = {
			practitionerOptions: [],
			selectedOption: null,
			form: {
				practAgreement: {
					value: this.props.gearOrder ? this.props.gearOrder.practAgreement : null
				}
			}
		}

		this.disableInputs = this.disableInputs.bind(this);
		this.updatePractitioner = this.updatePractitioner.bind(this);
		this.togglePractAgreement = this.togglePractAgreement.bind(this);
		this.goBack = this.goBack.bind(this);
		this.validate = this.validate.bind(this);
		this.save = this.save.bind(this);
	}

	componentDidMount() {
		this.loadPractitionerOptions();
	}

	loadPractitionerOptions = () => {
		fetchPractitionerOptions()
		.then(json => {
			let practitionerOptions = json;
			let selectedOption;
			if (this.props.gearOrder && this.props.gearOrder.id !== 0) {
				if (json) {
					selectedOption = json.find(option => option.practitioner.gsdlNo === this.props.gearOrder.practitioner.gsdlNo)
				}

				// For admin users, the order's practitioner will not match any items in practitionerOptions. Use the order's value.
				if (!selectedOption) {
					selectedOption = {
						office: this.props.gearOrder.practitioner.office,
						practitioner: this.props.gearOrder.practitioner
					}
					practitionerOptions = [selectedOption];
				} 
				
			} else if (json) {
				selectedOption = json[0];
			}

			this.setState({
				practitionerOptions: practitionerOptions,
				selectedOption: selectedOption
			});
		})
		.catch(e => {
			this.props.showError('The network is currently unavailable. Please call us at ' + getCustomerCareNumber(this.props?.gearOrder) + '.');
		});
	}

	/**
	 * Determines if the user can authorize the order. Authorization is allowed if:
	 *  - The selected practitioner is self authorized
	 *  - The logged in user has access "On Behalf Of" the order's authorizing doctor (That includes the logged in user being the authorizing doctor).
	 */
	 authorizingPractitioner() {
		const onBehalfOf = this.props.userInfo.onBehalfOf?.split(',') ?? [];
		return (
			this.props.userInfo.gsdlNo ===
				this.state.selectedOption.practitioner.authorizingDoctorGsdlNo ||
			onBehalfOf.includes(this.state.selectedOption.practitioner.authorizingDoctorGsdlNo)
		);
	}

	/**
	 * Determines if there an authorizing practitioner for the selected practitioner account.
	 */
	authorizingPractitionerId() {
		return this.state.selectedOption.practitioner.authorizingDoctorGsdlNo;
	}

	 isCreditStatusCOD(practitioner) {
		 return (practitioner?.cmsCreditStatus?.creditStatus === 'C.O.D Credit Hold');
	}

	/**
	 * Determines if the inputs need to be disabled. Even after accessioning, the inputs will remain enabled for the
	 * authorizing practitioner if the practitioner agreement has not been made.
	 * The step is never disabled for an admin user.
	 */
	disableInputs() {
		const accessioned = this.props.gearOrder && this.props.gearOrder.requisitions && this.props.gearOrder.requisitions.some(req => req.accessionNo);

		const practConfirmStep = this.props.getWorkflowStep('practitioner_confirmation');
		const shipToOfficeConfirmed = this.props.gearOrder && this.props.gearOrder.containsShipToOfficeRequisition && practConfirmStep && practConfirmStep.completed;

		return (this.props.userInfo.adminUser !== 'true' && this.props.gearOrder.practAgreement !== null && (accessioned || shipToOfficeConfirmed)) || false;
	}

	/**
	 * Updates the selected practitioner.
	 *
	 * @param e the DOM event
	 */
	updatePractitioner(e) {
		this.setState({
			selectedOption: this.state.practitionerOptions.find(option => option.practitioner.gsdlNo === e.currentTarget.value)
		});
		this.props.setDirty(true);
	}

	/**
	 * Toggles the practitioner agreement.
	 *
	 * @param e the DOM event
	 */
	togglePractAgreement(e) {
		let form = Object.assign({}, this.state.form);
		form.practAgreement.value = e.currentTarget.checked;
		this.setState({
			form: this.validate(form)
		});
		this.props.setDirty(true);
	}

	/**
	 * Goes back to the previous step.
	 */
	goBack(e) {
        if (this.props.doConfirm(e)){
            this.props.updateStep(this.props.gearOrder, 'practitioner', 'Back');
        } else {
            e.preventDefault();
        }
	}
	
	validate(form) {
		Object.values(form).forEach(prop => {
			prop.valid = true;
			prop.message = "";
		});
		if (this.authorizingPractitioner() && !form.practAgreement.value) {
			form.practAgreement.valid = false;
			form.practAgreement.message = "Please verify you are authorized to order.";
		}
		
		return form;
	}

	/**
	 * Saves the practitioner step.
	 */
	save(event) {
		event.preventDefault();
		if (this.disableInputs()) {
			this.props.updateStep(this.props.gearOrder, 'practitioner', 'Next');
		} else {
			this.setState({
				form: this.validate(this.state.form)
			}, () => {
				if (Object.values(this.state.form).every(prop => prop.valid)) {
					let params = new URLSearchParams(this.props.location.search.substring(1));
					let source = params.get('source');
					let practitioner = {
						...this.state.selectedOption.practitioner,
						office: this.state.selectedOption.office
					};
					this.props.updatePractitionerInfo(practitioner, this.state.form.practAgreement.value, source);
					let updatedOrder = Object.assign({}, this.props.gearOrder);
					updatedOrder.practAgreement = this.state.form.practAgreement.value;
					updatedOrder.practitioner = practitioner;
					
					if (this.props.gearOrder.id !== 0) {
						fetch(process.env.REACT_APP_GEAR_REST_URL+'/practitioner/', {
							credentials: 'include',
							mode: 'cors',
							method: 'PUT',
							timeout:1000,
							headers: {
								'Accept': 'application/json',
								'Content-Type': 'application/json',
							},
							body: JSON.stringify(updatedOrder)
						})
						.then(response => {
							if (response.ok) {
								return response.json();
							} else {
								this.props.showError("An unexpected error has occurred. Please call us at " + getCustomerCareNumber(this.props?.gearOrder) + ".");
							}
						})
						.then(json => {
							if (json.id){
								this.props.setGearOrder(json);
								this.props.setDirty(false);
								this.props.updateStep(json, 'practitioner', 'Next');
							}
						})
						.catch(error => {
							this.props.showError("The network is currently unavailable. Please call us at " + getCustomerCareNumber(this.props?.gearOrder) + ".");
						});
					} else {
						this.props.updateStep(this.props.gearOrder, 'practitioner', 'Next');
					}
				} else {
					scrollToMessage();
				}
			});
		}
	}

	/**
	 * Push the practitioner into the list of practitioner options and set this as the selected practitioner.
	 * The push will only occurr if the practitioner is not already in the list.
	 *
	 * @param practitioner The practitioner details to select
	 */
	handleAdminPractitionerSearchResults = (practitioner) => {
		if (!practitioner) {
			return;
		}

		// Convert the practitioner into a PractitionerOption
		const newOption = {
			practitioner: practitioner,
			office: practitioner.office
		};

		this.setState({
			selectedOption: newOption
		});

		if (!this.state.practitionerOptions.some(option => option.practitioner.gsdlNo === practitioner.gsdlNo)) {
			this.setState({
				practitionerOptions: [...this.state.practitionerOptions, newOption]
			});
		}
	}

	/**
	 * Renders the component.
	 */
	render() {
		// do not render if the options, user, or order have not loaded yet
		if (!this.state.selectedOption || !this.props.userInfo || !this.props.gearOrder) {
			return null;
		}

		const disableInputs = this.disableInputs();
		const accessioned = this.props.gearOrder && this.props.gearOrder.requisitions && this.props.gearOrder.requisitions.some(req => req.accessionNo);

		const isAdmin = this.props.userInfo.adminUser === 'true';
		const isNewOrder = this.props.match.params.id === 'new';

		const creditStatusCOD = this.isCreditStatusCOD(this.state.selectedOption.practitioner);
		const nextBtnDisabled = creditStatusCOD;
		
		return (
			<div>
				<div className="row">
					<div className="col-md-12">
						<div>
							<h2>Practitioner</h2>
							<p>Ensure that the correct business practice appears below and select the appropriate ordering practitioner's name from the drop-down.</p>
							{(disableInputs && accessioned) && (
								<AccessionedOrderAlert isUKOrder={isUKOrder(this.props.gearOrder)} />
							)}
							{(disableInputs && !accessioned) && (
								<AlertMessage display={disableInputs} />
							)}
						</div>
					</div>
				</div>
				<div className="space-top"></div>
				<div className="row">
					<div className="col-12 col-sm-6 col-md-4 col-lg-4">
						<div className="well">
							<h4>{this.state.selectedOption.office.name} </h4>
							<ul className="list">
								<li>
									<i className="fa fa-hospital-o pe-1"></i>{this.state.selectedOption.office.address.address1}  <br/>
									{this.state.selectedOption.office.address.address2 && <span className="ps-3">{this.state.selectedOption.office.address.address2} <br/></span>}
									<span className="ps-3">{this.state.selectedOption.office.address.city},  {this.state.selectedOption.office.address.state}  {this.state.selectedOption.office.address.zipCode}</span>
								</li>
								<li>
									<i className="fa fa-phone pe-1"></i>
									<abbr title="Phone">P: </abbr>
									{this.state.selectedOption.office.phoneNumber}
								</li>
							</ul>
						</div>
					</div>
					<div className="col-12 col-sm-6 col-md-6 offset-md-1 col-lg-6 offset-lg-1">
						<h4>Please Select Practitioner Account:</h4>
						<form id="practitioner-form" noValidate>
							<select className="form-select"
									id="pract-select"
									value={(this.state.selectedOption && this.state.selectedOption.practitioner.gsdlNo) || ''}
									onChange={this.updatePractitioner}
									disabled={disableInputs}>
								{this.state.practitionerOptions.map(function(option) {
									return 	<option key={option.practitioner.gsdlNo} value={option.practitioner.gsdlNo}>
												{option.practitioner.firstName} {option.practitioner.lastName}, {option.practitioner.degree} ({option.practitioner.gsdlNo})
											</option>
								})}
							</select>
							{this.authorizingPractitioner() && !creditStatusCOD &&
								<div className={"space-top form-group " + (this.state.form.practAgreement.valid === false ? "has-error" : "")}>
									<div className="checkbox">
										<label className="control-label" htmlFor="practCheckBox">
											<input id="practCheckBox"
												   type="checkbox"
												   value="agree"
												   defaultChecked={this.props.gearOrder.practAgreement}
												   onClick={this.togglePractAgreement}
												   disabled={disableInputs} 
											/>
											<strong>I agree that I am this practitioner, or that I am authorized to order on their behalf.<span className="text-danger">*</span></strong>
										</label>
										{this.state.form.practAgreement.valid === false &&
											<span className="validation-message text-danger">{this.state.form.practAgreement.message}</span>
										}
									</div>
								</div>
							}
							{creditStatusCOD &&
								<div className="space-top">
								<div className="alert alert-danger alert-icon-warning mt-10" role="alert">
									<strong>Important Notice:</strong> Your account is currently on credit hold, and online ordering is temporarily unavailable. To restore access, please contact Genova to resolve your outstanding balance. <br/> We're here to help if you have any questions, {getCustomerCareNumber(this.props?.gearOrder)}.
							  	</div>
							  	</div>	
							}
							{!this.authorizingPractitionerId() &&
								<div className="space-top">
								<div className="alert alert-warning" role="alert">
							  		<strong>This account is for demo purposes only.</strong><br/>
							  		If you feel you are seeing this in error, please call us at {getCustomerCareNumber(this.props?.gearOrder)}.
							  	</div>
							  	</div>	
							}
						</form>
						{(isAdmin && isNewOrder) && (
							<div className="panel panel-primary">
								<div className="panel-heading">Admin</div>
								<div className="panel-body">
									<label>Search for a practitioner by ID.</label><br />
									<AdminPractitionerSearch onSubmit={this.handleAdminPractitionerSearchResults} />
								</div>
							</div>
						)}
					</div>
				</div>
				<div className="space-bottom"></div>
				<RequiredFieldDisclaimer />
				<div className="col-md-12 col-12 sticky-bottom">
					<button type="button" className="btn btn-white rounded-pill btn-sm margin-clear" onClick={(e) => this.goBack(e)}>
						<i className="pe-2 fa fa-arrow-circle-left"></i>Back
					</button>
					<button type="submit" form="practitioner-form" className="btn btn-primary rounded-pill btn-sm margin-clear pull-right" 
						disabled={nextBtnDisabled} onClick={this.save}>
						Next <i className="ps-2 fa fa-arrow-circle-right"></i>
					</button>
				</div>
			</div>
		);
	}
}

// wrap the component in the workflow HOC to get access to common workflow logic
export default withWorkflow(OfficeInfo);
